/**
 * Serviço de Audio Fingerprinting
 * Responsável por gerar fingerprints únicos usando Web Audio API
 */

export class AudioService {
    constructor(config, logger) {
        this.config = config;
        this.logger = logger.child('AudioService');
        this.cache = new Map();
        this.stats = {
            calls: 0,
            errors: 0,
            cacheHits: 0,
            avgExecutionTime: 0
        };
        this.audioContext = null;
    }

    /**
     * Obtém fingerprint de áudio
     */
    async getFingerprint() {
        if (!this.config.audioEnabled) {
            this.logger.debug('Audio fingerprinting desabilitado');
            return { enabled: false };
        }

        // Check if user has interacted with the page to avoid autoplay warnings
        if (!this.hasUserInteracted()) {
            this.logger.debug('Audio fingerprinting requires user interaction, skipping for now');
            return { enabled: false, status: 'skipped_no_interaction', hash: 'skipped' };
        }

        const startTime = performance.now();
        this.stats.calls++;

        try {
            // Verifica cache primeiro
            const cacheKey = 'audio_fingerprint';
            if (this.cache.has(cacheKey)) {
                this.stats.cacheHits++;
                this.logger.debug('Audio fingerprint obtido do cache');
                return this.cache.get(cacheKey);
            }

            const fingerprint = await this.generateAudioFingerprint();

            // Armazena no cache
            this.cache.set(cacheKey, fingerprint);

            const executionTime = performance.now() - startTime;
            this.updateStats(executionTime);

            this.logger.debug('Audio fingerprint gerado', {
                executionTime: `${executionTime.toFixed(2)}ms`,
                sampleRate: fingerprint.context_info?.sampleRate
            });

            return fingerprint;

        } catch (error) {
            this.stats.errors++;
            this.logger.error('Erro ao gerar audio fingerprint', { error: error.message });
            throw error;
        }
    }

    /**
     * Gera fingerprint completo de áudio
     */
    async generateAudioFingerprint() {
        const audioContext = this.getAudioContext();

        if (!audioContext) {
            throw new Error('Web Audio API não disponível');
        }

        const fingerprint = {
            // Informações do contexto
            context_info: this.getContextInfo(audioContext),

            // Capacidades de áudio
            capabilities: this.getAudioCapabilities(audioContext),

            // Fingerprint de oscilador
            oscillator_fingerprint: await this.generateOscillatorFingerprint(audioContext),

            // Fingerprint de convolução
            convolution_fingerprint: await this.generateConvolutionFingerprint(audioContext),

            // Fingerprint de compressor dinâmico
            compressor_fingerprint: await this.generateCompressorFingerprint(audioContext),

            // Análise de frequência
            frequency_analysis: await this.generateFrequencyAnalysis(audioContext),

            // Hash único
            hash: null // Será calculado no final
        };

        // Calcula hash do fingerprint completo
        fingerprint.hash = await this.hashFingerprint(fingerprint);

        return fingerprint;
    }

    /**
     * Obtém contexto de áudio
     */
    /**
     * Obtém contexto de áudio (Offline para evitar warnings e melhorar performance)
     */
    getAudioContext() {
        // Sempre cria um novo contexto offline para garantir isolamento
        try {
            const AudioContextClass = window.OfflineAudioContext || window.webkitOfflineAudioContext;

            if (!AudioContextClass) {
                return null;
            }

            // 1 canal, 44100 comprimento, 44100 sample rate
            // Renderiza 1 segundo de áudio (suficiente para fingerprint)
            return new AudioContextClass(1, 44100, 44100);

        } catch (error) {
            this.logger.error('Erro ao criar contexto de áudio', { error: error.message });
            return null;
        }
    }

    /**
     * Obtém informações do contexto de áudio
     */
    getContextInfo(audioContext) {
        return {
            sampleRate: audioContext.sampleRate,
            state: audioContext.state,
            maxChannelCount: audioContext.destination.maxChannelCount,
            numberOfInputs: audioContext.destination.numberOfInputs,
            numberOfOutputs: audioContext.destination.numberOfOutputs,
            channelCount: audioContext.destination.channelCount,
            channelCountMode: audioContext.destination.channelCountMode,
            channelInterpretation: audioContext.destination.channelInterpretation,
            baseLatency: audioContext.baseLatency || null,
            outputLatency: audioContext.outputLatency || null,
            currentTime: audioContext.currentTime
        };
    }

    /**
     * Obtém capacidades de áudio
     */
    getAudioCapabilities(audioContext) {
        const capabilities = {
            supported_nodes: [],
            supported_methods: [],
            constructor_names: []
        };

        // Testa nós de áudio disponíveis
        const nodeTypes = [
            'createOscillator', 'createGain', 'createDelay', 'createBiquadFilter',
            'createConvolver', 'createAnalyser', 'createScriptProcessor',
            'createStereoPanner', 'createChannelSplitter', 'createChannelMerger',
            'createDynamicsCompressor', 'createWaveShaper', 'createPanner',
            'createPeriodicWave', 'createBufferSource', 'createMediaElementSource',
            'createMediaStreamSource', 'createIIRFilter'
        ];

        nodeTypes.forEach(nodeType => {
            try {
                if (typeof audioContext[nodeType] === 'function') {
                    capabilities.supported_nodes.push(nodeType);

                    // Testa criação do nó
                    const node = audioContext[nodeType]();
                    if (node) {
                        capabilities.constructor_names.push(node.constructor.name);

                        // Desconecta o nó
                        if (typeof node.disconnect === 'function') {
                            node.disconnect();
                        }
                    }
                }
            } catch (error) {
                // Nó não suportado ou erro na criação
            }
        });

        // Testa métodos disponíveis
        const methods = [
            'suspend', 'resume', 'close', 'createBuffer',
            'decodeAudioData', 'createMediaElementSource'
        ];

        methods.forEach(method => {
            if (typeof audioContext[method] === 'function') {
                capabilities.supported_methods.push(method);
            }
        });

        return capabilities;
    }

    /**
     * Gera fingerprint usando oscilador
     */
    async generateOscillatorFingerprint(ignoredContext) {
        // Create local offline context for isolation
        const audioContext = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 44100, 44100);

        return new Promise((resolve, reject) => {
            try {
                const oscillator = audioContext.createOscillator();
                const analyser = audioContext.createAnalyser();
                const gain = audioContext.createGain();

                // Configuração
                oscillator.type = 'triangle';
                oscillator.frequency.value = 1000;
                gain.gain.value = 0.1;

                // Conecta nós
                oscillator.connect(analyser);
                analyser.connect(gain);
                gain.connect(audioContext.destination);

                // Inicia oscilador
                oscillator.start(0);

                audioContext.startRendering().then((renderedBuffer) => {
                    // Analise PCM data from rendered buffer
                    const pcmData = renderedBuffer.getChannelData(0);
                    const bufferLength = 2048; // Use equivalent size
                    const dataArray = new Uint8Array(bufferLength);

                    // Simulate frequency data extraction or just use time domain
                    const pcmSlice = pcmData.slice(0, bufferLength);
                    for (let i = 0; i < pcmSlice.length; i++) {
                        dataArray[i] = Math.abs(pcmSlice[i]) * 255;
                    }

                    const fingerprint = {
                        frequency_data: Array.from(dataArray.slice(0, 50)),
                        checksum: this.calculateArrayChecksum(dataArray),
                        peak_frequency: 1000,
                        rms: this.calculateRMS(dataArray),
                        oscillator_type: oscillator.type,
                        frequency: oscillator.frequency.value
                    };

                    resolve(fingerprint);
                }).catch(reject);

            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Gera fingerprint usando convolução
     */
    async generateConvolutionFingerprint(audioContext) {
        try {
            const convolver = audioContext.createConvolver();
            const buffer = audioContext.createBuffer(2, audioContext.sampleRate * 0.1, audioContext.sampleRate);

            // Cria impulse response sintético
            for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
                const channelData = buffer.getChannelData(channel);
                for (let i = 0; i < channelData.length; i++) {
                    channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / channelData.length, 2);
                }
            }

            convolver.buffer = buffer;

            // Testa propriedades do convolver
            const fingerprint = {
                buffer_length: buffer.length,
                buffer_duration: buffer.duration,
                buffer_sampleRate: buffer.sampleRate,
                numberOfChannels: buffer.numberOfChannels,
                normalize: convolver.normalize,
                buffer_checksum: this.calculateBufferChecksum(buffer)
            };

            convolver.disconnect();

            return fingerprint;

        } catch (error) {
            return { error: error.message };
        }
    }

    /**
     * Gera fingerprint usando compressor dinâmico
     */
    async generateCompressorFingerprint(audioContext) {
        try {
            const compressor = audioContext.createDynamicsCompressor();

            // Configura parâmetros do compressor
            compressor.threshold.setValueAtTime(-24, audioContext.currentTime);
            compressor.knee.setValueAtTime(30, audioContext.currentTime);
            compressor.ratio.setValueAtTime(12, audioContext.currentTime);
            compressor.attack.setValueAtTime(0.003, audioContext.currentTime);
            compressor.release.setValueAtTime(0.25, audioContext.currentTime);

            const fingerprint = {
                threshold: compressor.threshold.value,
                knee: compressor.knee.value,
                ratio: compressor.ratio.value,
                attack: compressor.attack.value,
                release: compressor.release.value,
                reduction: compressor.reduction || 0
            };

            compressor.disconnect();

            return fingerprint;

        } catch (error) {
            return { error: error.message };
        }
    }

    /**
     * Gera análise de frequência
     */
    async generateFrequencyAnalysis(audioContext) {
        // Since we are using OfflineContext, we can't reuse the same context easily for multiple renderings 
        // without recreating it or handling state. However, the previous method getAudioContext always returns a new one.
        // But generating ALL fingerprints in one go is better. For now we assume generateAudioFingerprint calls this.
        // Wait, generateAudioFingerprint gets one context and passes it around.
        // OfflineAudioContext can only startRendering ONCE.
        // This means we must refactor generateAudioFingerprint to not share the context if we render in each sub-method,
        // OR construct the entire graph for all tests and render once.

        // Given complexity, better to create a new context here or simplify.
        // Let's create a local context for this specific test to ensure isolation and proper rendering.

        const localContext = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 44100, 44100);

        return new Promise((resolve, reject) => {
            try {
                const oscillator1 = localContext.createOscillator();
                const oscillator2 = localContext.createOscillator();
                const gain = localContext.createGain();
                // Analyser is less useful in offline, but we can inspect the buffer

                oscillator1.type = 'sine';
                oscillator1.frequency.value = 440;

                oscillator2.type = 'sawtooth';
                oscillator2.frequency.value = 660;

                gain.gain.value = 0.05;

                // Connect
                oscillator1.connect(gain);
                oscillator2.connect(gain);
                gain.connect(localContext.destination);

                oscillator1.start(0);
                oscillator2.start(0);

                localContext.startRendering().then(renderedBuffer => {
                    const pcmData = renderedBuffer.getChannelData(0);
                    // Create pseudo-frequency data from PCM (time domain) as fallback
                    const bufferLength = 2048;
                    const frequencyData = new Uint8Array(bufferLength);
                    const timeDomainData = new Uint8Array(bufferLength);

                    const pcmSlice = pcmData.slice(0, bufferLength);
                    for (let i = 0; i < pcmSlice.length; i++) {
                        const val = Math.abs(pcmSlice[i]) * 255;
                        frequencyData[i] = val;
                        timeDomainData[i] = val;
                    }

                    const analysis = {
                        frequency_peaks: this.findFrequencyPeaks(frequencyData, 5),
                        frequency_checksum: this.calculateArrayChecksum(frequencyData),
                        time_domain_checksum: this.calculateArrayChecksum(timeDomainData),
                        spectral_centroid: this.calculateSpectralCentroid(frequencyData),
                        spectral_rolloff: this.calculateSpectralRolloff(frequencyData),
                        zero_crossing_rate: this.calculateZeroCrossingRate(timeDomainData),
                        analyser_config: {
                            fftSize: 2048,
                            frequencyBinCount: 1024,
                            minDecibels: -100,
                            maxDecibels: -30,
                            smoothingTimeConstant: 0.8
                        }
                    };
                    resolve(analysis);

                }).catch(reject);

            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Calcula checksum de array
     */
    calculateArrayChecksum(array) {
        let checksum = 0;
        for (let i = 0; i < array.length; i++) {
            checksum += array[i];
        }
        return checksum;
    }

    /**
     * Encontra frequência de pico
     */
    findPeakFrequency(frequencyData, sampleRate, fftSize) {
        let maxIndex = 0;
        let maxValue = 0;

        for (let i = 0; i < frequencyData.length; i++) {
            if (frequencyData[i] > maxValue) {
                maxValue = frequencyData[i];
                maxIndex = i;
            }
        }

        return (maxIndex * sampleRate) / fftSize;
    }

    /**
     * Calcula RMS (Root Mean Square)
     */
    calculateRMS(array) {
        let sum = 0;
        for (let i = 0; i < array.length; i++) {
            sum += array[i] * array[i];
        }
        return Math.sqrt(sum / array.length);
    }

    /**
     * Calcula checksum de buffer de áudio
     */
    calculateBufferChecksum(buffer) {
        let checksum = 0;

        for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
            const channelData = buffer.getChannelData(channel);
            for (let i = 0; i < channelData.length; i += 100) { // Amostra para performance
                checksum += Math.abs(channelData[i]) * 1000;
            }
        }

        return Math.floor(checksum);
    }

    /**
     * Encontra picos de frequência
     */
    findFrequencyPeaks(frequencyData, numPeaks) {
        const peaks = [];
        const threshold = Math.max(...frequencyData) * 0.3; // 30% do valor máximo

        for (let i = 1; i < frequencyData.length - 1; i++) {
            if (frequencyData[i] > threshold &&
                frequencyData[i] > frequencyData[i - 1] &&
                frequencyData[i] > frequencyData[i + 1]) {
                peaks.push({ index: i, value: frequencyData[i] });
            }
        }

        // Ordena por valor e retorna os maiores picos
        return peaks
            .sort((a, b) => b.value - a.value)
            .slice(0, numPeaks)
            .map(peak => peak.index);
    }

    /**
     * Calcula centroide espectral
     */
    calculateSpectralCentroid(frequencyData) {
        let weightedSum = 0;
        let magnitudeSum = 0;

        for (let i = 0; i < frequencyData.length; i++) {
            weightedSum += i * frequencyData[i];
            magnitudeSum += frequencyData[i];
        }

        return magnitudeSum > 0 ? weightedSum / magnitudeSum : 0;
    }

    /**
     * Calcula rolloff espectral
     */
    calculateSpectralRolloff(frequencyData, threshold = 0.85) {
        const totalEnergy = frequencyData.reduce((sum, value) => sum + value, 0);
        const rolloffEnergy = totalEnergy * threshold;

        let cumulativeEnergy = 0;
        for (let i = 0; i < frequencyData.length; i++) {
            cumulativeEnergy += frequencyData[i];
            if (cumulativeEnergy >= rolloffEnergy) {
                return i;
            }
        }

        return frequencyData.length - 1;
    }

    /**
     * Calcula taxa de cruzamento por zero
     */
    calculateZeroCrossingRate(timeDomainData) {
        let crossings = 0;
        const centerValue = 128; // Valor central para dados de 8 bits

        for (let i = 1; i < timeDomainData.length; i++) {
            if ((timeDomainData[i - 1] < centerValue && timeDomainData[i] >= centerValue) ||
                (timeDomainData[i - 1] >= centerValue && timeDomainData[i] < centerValue)) {
                crossings++;
            }
        }

        return crossings / timeDomainData.length;
    }

    /**
     * Calcula hash do fingerprint completo
     */
    async hashFingerprint(fingerprint) {
        const data = JSON.stringify({
            context_info: fingerprint.context_info,
            oscillator_checksum: fingerprint.oscillator_fingerprint?.checksum,
            convolution_checksum: fingerprint.convolution_fingerprint?.buffer_checksum,
            compressor_config: fingerprint.compressor_fingerprint,
            frequency_checksum: fingerprint.frequency_analysis?.frequency_checksum
        });

        try {
            if (crypto && crypto.subtle) {
                const encoder = new TextEncoder();
                const dataBuffer = encoder.encode(data);
                const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            }
        } catch (error) {
            this.logger.debug('Web Crypto API não disponível para hash de fingerprint');
        }

        // Fallback para hash simples
        let hash = 0;
        for (let i = 0; i < data.length; i++) {
            const char = data.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return Math.abs(hash).toString(16);
    }

    /**
     * Atualiza estatísticas de performance
     */
    updateStats(executionTime) {
        const totalTime = this.stats.avgExecutionTime * (this.stats.calls - 1) + executionTime;
        this.stats.avgExecutionTime = totalTime / this.stats.calls;
    }

    /**
     * Obtém estatísticas do serviço
     */
    getStats() {
        return {
            ...this.stats,
            cacheSize: this.cache.size,
            successRate: this.stats.calls > 0 ?
                ((this.stats.calls - this.stats.errors) / this.stats.calls * 100).toFixed(2) + '%' : '0%'
        };
    }

    /**
     * Limpa cache
     */
    clearCache() {
        this.cache.clear();
        this.logger.debug('Cache do AudioService limpo');
    }

    /**
     * Atualiza configuração
     */
    updateConfig(newConfig) {
        this.config = newConfig;
        if (!newConfig.audioEnabled) {
            this.clearCache();
        }
        this.logger.debug('Configuração do AudioService atualizada');
    }

    /**
     * Testa suporte ao Web Audio API
     */
    static isSupported() {
        try {
            const AudioContextClass = window.AudioContext ||
                window.webkitAudioContext ||
                window.mozAudioContext ||
                window.msAudioContext;
            return !!AudioContextClass;
        } catch {
            return false;
        }
    }

    /**
     * Obtém capacidades de áudio
     */
    static getCapabilities() {
        if (!AudioService.isSupported()) {
            return { supported: false };
        }

        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const tempContext = new AudioContextClass();

            const capabilities = {
                supported: true,
                sampleRate: tempContext.sampleRate,
                maxChannelCount: tempContext.destination.maxChannelCount,
                state: tempContext.state,
                baseLatency: tempContext.baseLatency || null,
                outputLatency: tempContext.outputLatency || null
            };

            tempContext.close();

            return capabilities;

        } catch (error) {
            return {
                supported: false,
                error: error.message
            };
        }
    }

    /**
     * Check if user has interacted with the page
     */
    hasUserInteracted() {
        // Simple check for user interaction (click, touch, key press)
        return window.hasUserGesture ||
            document.hasStoredUserActivation ||
            (typeof navigator !== 'undefined' && navigator.userActivation && navigator.userActivation.hasBeenActive);
    }

    /**
     * Limpa recursos
     */
    destroy() {
        this.clearCache();

        if (this.audioContext) {
            try {
                if (this.audioContext.state !== 'closed') {
                    this.audioContext.close();
                }
            } catch (error) {
                this.logger.debug('Erro ao fechar contexto de áudio', { error: error.message });
            }

            this.audioContext = null;
        }

        this.logger.debug('AudioService destruído');
    }
}